import { useState, useEffect } from "react";
import axios from "axios";
import ButtonWarning from "../components/Button-warning";
import { getAllApiUrls } from "../config/api";
import IsLoading from "../components/IsLoading";
import { useAuth } from '../contexts/AuthContext';


const HowMuch = () => {
  const { userData } = useAuth();
  const [tokenContract, setTokenContract] = useState("");
  const [address, setAddress] = useState("");
  const [network, setNetwork] = useState("");
  const [notifModal, setNotifModal] = useState({
    isOpen: false,
    message: "",
    color: "",
  });
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [tokenSymbol, setTokenSymbol] = useState("");

  const handleUseUserAddress = () => {
    if (userData && userData.user.address) {
      setAddress(userData.user.address);
    }
  };

  const handleTokenContract = (event) => {
    setNotifModal({ isOpen: false, message: "", color: "" });
    const searchTokenContract = event.target.value;
    setTokenContract(searchTokenContract);
  };

  const handleAddress = (event) => {
    setNotifModal({ isOpen: false, message: "", color: "" });
    const searchAddress = event.target.value;
    setAddress(searchAddress);
  };

  const clearToken = () => {
    setTokenContract("");
    setAddress("");
    setNotifModal({ isOpen: false, message: "", color: "" });
    setTotalAmount(0);
    setTokenSymbol("");
  };

  const fetchData = async () => {
    if (!address || !tokenContract) return;

    try {
      setLoading(true);
      setTotalAmount(null);
      setTokenSymbol("");
      setNetwork("");
      setNotifModal({ isOpen: false, message: "", color: "" });

      const apiUrls = getAllApiUrls(tokenContract, address);

      for (const [currentNetwork, url] of Object.entries(apiUrls)) {
        try {
          const response = await axios.get(url);

          if (
            response.data &&
            response.data.result &&
            response.data.result.length > 0
          ) {
            calculateTotalFromResult(response.data.result);
            setNetwork(currentNetwork); // Définir le réseau
            setNotifModal({
              isOpen: true,
              message: `Token found on ${currentNetwork}`,
              color: "green",
            });
            return;
          }
        } catch (error) {
          console.error(`Erreur lors de l'appel à l'API ${currentNetwork}:`, error);
        }
      }

      setNotifModal({ isOpen: true, message: "Token not found", color: "red" });
    } catch (error) {
      console.error("Erreur générale lors des appels API:", error);
      setNotifModal({
        isOpen: true,
        message: "Error fetching data",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalFromResult = (result) => {
    let total = 0;
    let symbol = "";

    result.forEach((transaction) => {
      if (transaction.to.toLowerCase() === address.toLowerCase()) {
        total +=
          Number(transaction.value) /
          Math.pow(10, Number(transaction.tokenDecimal));
      }
      if (!symbol && transaction.tokenSymbol) {
        symbol = transaction.tokenSymbol;
      }
    });

    setTotalAmount(total);
    setTokenSymbol(symbol);

    return total;
  };

  useEffect(() => {
    if (address && tokenContract) {
      const timer = setTimeout(() => {
        fetchData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [address, tokenContract]);

  useEffect(() => {
    if (notifModal.isOpen) {
      const timer = setTimeout(() => {
        setNotifModal({ ...notifModal, isOpen: false });
      }, 4000);

      // Nettoyage du timer si le composant est démonté ou si notifModal change
      return () => clearTimeout(timer);
    }
  }, [notifModal]);

  const getExplorerUrl = () => {
    switch (network) {
      case 'ETH':
        return `https://etherscan.io/token/${tokenContract}?a=${address}`;
      case 'BSC':
        return `https://bscscan.com/token/${tokenContract}?a=${address}`;
      case 'MATIC':
        return `https://polygonscan.com/token/${tokenContract}?a=${address}`;
      case 'FTM':
        return `https://ftmscan.com/token/${tokenContract}?a=${address}`;
      case 'AVAX':
        return `https://snowtrace.io/token/${tokenContract}?a=${address}`;
      case 'ARB':
        return `https://arbiscan.io/token/${tokenContract}?a=${address}`;
      default:
        return '#';
    }
  };

  const FinalTotal = () => {
    if (totalAmount === null || !tokenSymbol) return null;

    const explorerUrl = getExplorerUrl();

    return (
      <div className="flex flex-col items-center space-y-2 text-xl font-bold">
        <div>
          {totalAmount.toFixed(4).toLocaleString()} {tokenSymbol}
        </div>
        <a 
          href={explorerUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-blue-500 hover:text-blue-700"
        >
          Voir toutes les transactions
        </a>
        <ButtonWarning text={"Clear"} func={clearToken} />
      </div>
    );
  };

  return (
    <div className="mt-5 space-y-4 m-auto sm:w-1/3 flex-col border-2 border-gray-200 rounded-md p-4 w-11/12 flex justify-around max-w-[400px]">
      {notifModal.isOpen && (
        <div
          className={`bg-${notifModal.color}-50 border-t-4 border-${notifModal.color}-500 rounded-b text-${notifModal.color}-900 px-4 py-3 shadow-md w-fit fixed right-10 top-[8rem] z-50`}
          role="alert">
          <div className="flex">
            <div className="py-1">
              <svg
                className="w-6 h-6 mr-4 text-teal-500 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20">
                <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
              </svg>
            </div>
            <div className="content-center">
              <p className="text-sm font-bold">{notifModal.message}</p>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="">
          Find out how much did you receive <strong>in TOTAL </strong>on a
          particular token
        </h1>
        <span>
          This tool works for{" "}
          <span className="font-semibold">
            Ethereum, Avalanche, Polygon, Fantom and BSC
          </span>{" "}
          tokens
        </span>
        <div>
          Please use{" "}
          <a
            href="https://www.coingecko.com/"
            target="_blank"
            rel="noreferrer"
            className="text-green-500 text-semibold hover:underline">
            CoinGecko
          </a>{" "}
          to find the right token contract
        </div>
      </div>

      <div className="flex flex-col items-center justify-around space-y-4">
        <div className="w-11/12 m-auto">
          <label className="label-1">Token Contract Address</label>
          <input
            type="text"
            placeholder="Token Contract Address"
            value={tokenContract}
            onChange={handleTokenContract}
            className="input-1"
          />
        </div>
        <div className="w-11/12 m-auto">
          <label className="label-1">ERC20 Address</label>
          <input
            type="text"
            placeholder="ERC20 Address"
            value={address}
            onChange={handleAddress}
            className="input-1"
          />
          {userData && userData.user && userData.user.address && (
            <button
              onClick={handleUseUserAddress}
              className="flex px-2 py-1 mt-4 ml-auto text-xs text-white transition-colors bg-blue-500 rounded right-2 top-1/2 hover:bg-blue-600"
            >
              Utiliser mon adresse
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="relative flex items-center justify-center h-full">
          <IsLoading />
        </div>
      ) : (
        <FinalTotal />
      )}
    </div>
  );
};

export default HowMuch;
